import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { NAV, SITE } from "@/data/site";
import SearchDialog from "@/components/SearchDialog";
import LanguageSelector from "@/components/LanguageSelector";
import { useLang } from "@/context/LanguageContext";
import { useBranding } from "@/context/BrandingContext";
import { useSiteSettings } from "@/context/SiteContext";
import { mediaUrl } from "@/lib/api";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { lang } = useLang();
  const { logo_url, alt_text } = useBranding();
  const settings = useSiteSettings();
  const publisherName = settings?.publisher_name || SITE.name;
  const markLine = settings?.mark_line || "Est. Publications · Bharat";
  const nav = settings?.nav?.length ? settings.nav : NAV;
  const showTranslationNotice = lang !== "en";

  return (
    <>
      <header
        className="sticky top-0 z-40 border-b"
        style={{ background: "rgba(250, 249, 246, 0.92)", backdropFilter: "blur(14px)", borderColor: "var(--gi-border)" }}
        data-testid="site-header"
      >
        {showTranslationNotice && (
          <div className="text-center text-[11px] py-2" style={{ background: "var(--gi-ivory-2)", color: "var(--gi-muted)", borderBottom: "1px solid var(--gi-border)" }} data-testid="translation-notice">
            {lang === "hi" ? "पूर्ण अनुवाद शीघ्र आ रहा है। इस बीच अंग्रेज़ी सामग्री प्रदर्शित की जा रही है।" : "সম্পূর্ণ অনুবাদ শীঘ্রই আসছে। ততক্ষণ ইংরেজি বিষয়বস্তু দেখানো হচ্ছে।"} <span className="opacity-70">· Translation coming soon</span>
          </div>
        )}
        <div className="container-wide flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="site-logo" aria-label={`${publisherName} — Home`}>
            {logo_url ? (
              <img
                src={mediaUrl(logo_url)}
                alt={alt_text}
                className="w-auto object-contain h-[48px] max-w-[160px] sm:h-[60px] sm:max-w-[220px]"
                data-testid="site-logo-image"
              />
            ) : (
              <>
                <div className="w-9 h-9 flex items-center justify-center border" style={{ borderColor: "var(--gi-gold)", color: "var(--gi-maroon)" }}>
                  <span className="font-accent text-[11px]" style={{ letterSpacing: "0.14em" }}>GI</span>
                </div>
                <div className="leading-tight">
                  <div className="font-heading text-[19px]" style={{ color: "var(--gi-maroon)" }} data-testid="header-publisher-name">{publisherName}</div>
                  <div className="font-accent text-[9px]" style={{ color: "var(--gi-muted)" }} data-testid="header-mark-line">{markLine}</div>
                </div>
              </>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {nav.filter(n => !["Join the Mission", "Membership", "Media", "Articles", "Events"].includes(n.label) && n.label !== "Membership").map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
            <MoreMenu nav={nav} />
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={() => setSearchOpen(true)} aria-label="Search" className="p-2 nav-link hidden sm:flex" data-testid="header-search-btn">
              <Search size={16} />
            </button>
            <div className="hidden md:block"><LanguageSelector compact /></div>
            <Link to="/join" className="btn-gold hidden sm:inline-flex" data-testid="header-join-mission-btn">
              Join the Mission
            </Link>
            <button
              className="lg:hidden p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t" style={{ borderColor: "var(--gi-border)" }} data-testid="mobile-nav">
            <div className="container-wide py-6 flex flex-col gap-4">
              <button onClick={() => { setSearchOpen(true); setOpen(false); }} className="nav-link flex items-center gap-2" data-testid="mobile-search-btn"><Search size={14} /> Search</button>
              <div className="my-2"><LanguageSelector /></div>
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/join" onClick={() => setOpen(false)} className="btn-gold mt-2 justify-center" data-testid="mobile-join-mission-btn">
                Join the Mission
              </Link>
            </div>
          </div>
        )}
      </header>
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

const MoreMenu = ({ nav }) => {
  const [open, setOpen] = useState(false);
  const items = (nav || NAV).filter(n => ["Join the Mission", "Media", "Articles", "Events"].includes(n.label));
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button
        onMouseEnter={() => setOpen(true)}
        onClick={() => setOpen(v => !v)}
        className="nav-link"
        data-testid="nav-more"
      >
        More
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 min-w-[220px] border shadow-lg"
          style={{ background: "#FFFFFF", borderColor: "var(--gi-border)" }}
          data-testid="nav-more-menu"
        >
          {items.map(i => (
            <NavLink
              key={i.to}
              to={i.to}
              className="block px-5 py-3 text-[13px] hover:bg-[var(--gi-ivory-2)]"
              onClick={() => setOpen(false)}
              data-testid={`nav-more-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={{ color: "var(--gi-charcoal)" }}
            >
              {i.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default Header;
